import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

/* ═══════════════════════════════════════════════════════════════════════════
   PIPELINE RING — Animated deal pipeline completion ring
   ═══════════════════════════════════════════════════════════════════════════ */
function PipelineRing({ targetScore = 78, animate = false }: { targetScore?: number; animate?: boolean }) {
  const [displayScore, setDisplayScore] = useState(0);
  useEffect(() => {
    if (!animate) return;
    let frame: number;
    const duration = 2500;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(eased * targetScore));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [animate, targetScore]);
  const circumference = 2 * Math.PI * 90;
  return (
    <div className="pipeline-ring" style={{ '--score': animate ? targetScore : 0 } as React.CSSProperties}>
      <svg width="220" height="220" viewBox="0 0 200 200">
        <defs>
          <linearGradient id="crmGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#60a5fa" />
            <stop offset="100%" stopColor="#93c5fd" />
          </linearGradient>
        </defs>
        <circle className="ring-track" cx="100" cy="100" r="90" />
        <circle className="ring-fill" cx="100" cy="100" r="90"
          style={{ strokeDasharray: circumference, strokeDashoffset: animate ? circumference - (circumference * targetScore) / 100 : circumference, transition: 'stroke-dashoffset 2.5s cubic-bezier(0.4, 0, 0.2, 1)' }} />
      </svg>
      <div className="pipeline-ring-value">
        <div className="stat-counter" style={{ fontSize: '3rem', fontWeight: 800, color: '#fff' }}>{displayScore}%</div>
        <div style={{ fontSize: '0.85rem', color: 'rgba(96, 165, 250, 0.7)', fontWeight: 500, marginTop: 4 }}>Win Rate</div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   AI LEAD SCORING DEMO
   ═══════════════════════════════════════════════════════════════════════════ */
function LeadScoringDemo() {
  const [phase, setPhase] = useState<'idle' | 'scanning' | 'scored'>('idle');
  const [visibleLeads, setVisibleLeads] = useState<number[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  const leads = [
    { name: 'Apex Construction Inc', score: 94, status: '🔥 Hot', industry: 'Construction', value: '$58,000', signal: 'Visited pricing 3x this week' },
    { name: 'Silicon Valley Properties', score: 72, status: '🌡️ Warm', industry: 'Real Estate', value: '$32,000', signal: 'Opened last 5 emails' },
    { name: 'Pacific Digital Agency', score: 45, status: '❄️ Cold', industry: 'Digital', value: '$15,000', signal: 'No activity in 14 days' },
    { name: 'Midwest Logistics Group', score: 88, status: '🔥 Hot', industry: 'Logistics', value: '$85,000', signal: 'Requested demo via form' },
  ];

  const startDemo = useCallback(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    setPhase('scanning');
    setTimeout(() => {
      setPhase('scored');
      leads.forEach((_, idx) => {
        setTimeout(() => setVisibleLeads(prev => [...prev, idx]), idx * 400);
      });
    }, 1200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTimeout(startDemo, 400); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startDemo]);

  return (
    <div ref={ref} style={{ width: '100%', maxWidth: 700, margin: '0 auto' }}>
      <div className="glass-card" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 12, borderBottom: '1px solid rgba(59,130,246,0.15)' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#60a5fa', animation: 'blink 2s ease-in-out infinite' }} />
          <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 500 }}>AI Lead Intelligence — Live Demo</span>
        </div>

        {phase === 'scanning' && (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.2rem', color: '#60a5fa', fontWeight: 600 }}>🔍 Scanning pipeline...</div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 8 }}>AI is analyzing engagement signals, email opens, and website activity</div>
          </div>
        )}

        {phase === 'scored' && (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {leads.map((lead, idx) => (
              visibleLeads.includes(idx) && (
                <div key={lead.name} className="lead-card-animate" style={{ animationDelay: `${idx * 0.1}s`, display: 'flex', alignItems: 'center', gap: 12, padding: '12px', borderRadius: 12, background: 'rgba(30, 37, 86, 0.4)', border: '1px solid rgba(59,130,246,0.1)' }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: `rgba(59,130,246,${lead.score / 100 * 0.3})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 700, color: lead.score > 70 ? '#60a5fa' : '#64748b', flexShrink: 0 }}>{lead.score}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#e2e8f0' }}>{lead.name}</span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{lead.value}</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{lead.status} · {lead.signal}</div>
                    <div className="lead-score-bar" style={{ marginTop: 6 }}>
                      <div className="lead-score-fill" style={{ width: `${lead.score}%`, background: lead.score > 80 ? '#3b82f6' : lead.score > 60 ? '#60a5fa' : '#475569' }} />
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STATS COUNTER
   ═══════════════════════════════════════════════════════════════════════════ */
function StatsCounter({ end, suffix = '', prefix = '' }: { end: number; suffix?: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!inView) return;
    let frame: number;
    const duration = 2000;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * end));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, end]);
  return <span ref={ref} className="stat-counter">{prefix}{count.toLocaleString()}{suffix}</span>;
}

/* ═══════════════════════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════════════════════ */
const FEATURES = [
  { icon: '🎯', title: 'AI Lead Scoring', description: 'Machine learning analyzes engagement patterns to score and prioritize your leads automatically.', gradient: 'linear-gradient(135deg, #3b82f6, #60a5fa)' },
  { icon: '📊', title: 'Pipeline Intelligence', description: 'Real-time pipeline analytics with AI-powered forecasting and deal risk assessment.', gradient: 'linear-gradient(135deg, #10b981, #34d399)' },
  { icon: '📧', title: 'Email Intelligence', description: 'AI reads email sentiment, suggests follow-ups, and identifies buying signals automatically.', gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)' },
  { icon: '🤖', title: 'Sales AI Assistant', description: 'Ask questions about your pipeline in natural language. Get instant, data-backed answers.', gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' },
  { icon: '🇺🇸', title: 'US Business Context', description: 'SEC filings, SBA lending data, state regulations — your CRM understands US business.', gradient: 'linear-gradient(135deg, #ef4444, #f87171)' },
  { icon: '🔗', title: 'Cross-Suite Connected', description: 'Linked to EF-Strategy, EF-CapFlow, EF-Link, and EF-Projex for 360° customer intelligence.', gradient: 'linear-gradient(135deg, #ec4899, #f472b6)' },
];

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN LANDING PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
export default function EFCRMLanding() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [heroInView, setHeroInView] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setHeroInView(true); }, { threshold: 0.1 });
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-root">
      {/* ─── NAV ─── */}
      <nav className={scrollY > 50 ? 'nav-glass' : ''} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, transition: 'all 0.3s ease', ...(scrollY <= 50 ? { background: 'transparent' } : {}) }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #3b82f6, #60a5fa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>🎯</div>
            <div>
              <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>EF-CRM</span>
              <span style={{ fontSize: '0.7rem', color: '#64748b', marginLeft: 8 }}>by Executive Force AI</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/login')} style={{ padding: '8px 16px', fontSize: '0.85rem', fontWeight: 500, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer' }}>Sign In</button>
            <button onClick={() => navigate('/login')} style={{ padding: '8px 20px', fontSize: '0.85rem', fontWeight: 600, color: '#fff', background: '#3b82f6', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Start Free Trial</button>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section ref={heroRef} className="grid-pattern" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5rem 1.5rem 3rem' }}>
        <div className="landing-orbs"><div className="landing-orb landing-orb--1" /><div className="landing-orb landing-orb--2" /><div className="landing-orb landing-orb--3" /></div>
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease 0.2s', marginBottom: 24 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(59,130,246,0.1)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.2)' }}>🎯 AI-Powered Sales Intelligence</span>
          </div>
          <h1 style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 800, lineHeight: 1.1, marginBottom: 24, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease 0.4s' }}>
            <span style={{ color: '#fff' }}>Your AI</span><br />
            <span className="text-gradient-crm">Sales Copilot</span>
          </h1>
          <p style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)', color: '#94a3b8', maxWidth: 600, margin: '0 auto 3rem', lineHeight: 1.6, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease 0.6s' }}>
            AI that scores leads, predicts deals, and coaches your team — increasing win rates by <span style={{ color: '#93c5fd', fontWeight: 600 }}>78%</span>.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 48, opacity: heroInView ? 1 : 0, transform: heroInView ? 'scale(1)' : 'scale(0.8)', transition: 'all 0.8s ease 0.8s' }}>
            <div className="animate-float"><PipelineRing targetScore={78} animate={heroInView} /></div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease 1.0s' }}>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ padding: '16px 32px', fontSize: '1.1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none', borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>Start 7-Day Free Trial 🚀</button>
            <button onClick={() => navigate('/login')} style={{ padding: '16px 32px', fontSize: '1.1rem', fontWeight: 500, color: '#94a3b8', background: 'transparent', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 12, cursor: 'pointer' }}>Sign In</button>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── LEAD SCORING DEMO ─── */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(59,130,246,0.1)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.2)', marginBottom: 16 }}>Live AI Demo</span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>AI That <span className="text-gradient-crm">Knows Your Pipeline</span></h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>Watch as AI scans your pipeline, analyzes engagement signals, and scores every lead in real-time.</p>
          </div>
          <LeadScoringDemo />
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── FEATURES ─── */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Sales Intelligence <span className="text-gradient-crm">Superpowers</span></h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>Everything your sales team needs to close more deals, faster.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
            {FEATURES.map((f) => (
              <div key={f.title} className="glass-card" style={{ padding: '1.5rem' }}>
                <div className="feature-icon-glow">
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: f.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', marginBottom: 16 }}>{f.icon}</div>
                </div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: '0.9rem', color: '#94a3b8', lineHeight: 1.6, margin: 0 }}>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── STATS ─── */}
      <section style={{ position: 'relative', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 32, textAlign: 'center' }}>
          {[
            { value: 78, suffix: '%', label: 'Higher Win Rate' },
            { value: 3, suffix: 'x', label: 'Faster Response' },
            { value: 500, suffix: '+', label: 'AI Actions/Month' },
            { value: 99, suffix: '.9%', label: 'Uptime' },
          ].map((s) => (
            <div key={s.label}>
              <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#fff', marginBottom: 4 }}><StatsCounter end={s.value} suffix={s.suffix} /></div>
              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── PRICING ─── */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Simple <span className="text-gradient-crm">Pricing</span></h2>
          <p style={{ fontSize: '1.1rem', color: '#94a3b8', marginBottom: 40 }}>7-day free trial. No credit card required. Cancel anytime.</p>
          <div className="glass-card" style={{ padding: '2.5rem', borderColor: 'rgba(59,130,246,0.4)' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>EF-CRM</div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
              <span style={{ fontSize: '3.5rem', fontWeight: 800, color: '#fff' }}>$49</span>
              <span style={{ fontSize: '1.1rem', color: '#64748b' }}>/month</span>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: 24 }}>500 AI actions · Lead scoring · Pipeline intelligence</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', textAlign: 'left' }}>
              {['AI Sales Assistant & deal insights', '500 AI actions per month', 'Pipeline forecasting & analytics', 'Automated follow-up suggestions', 'Lead scoring & qualification', 'SBA & SEC integration'].map((item) => (
                <li key={item} style={{ fontSize: '0.9rem', color: '#cbd5e1', padding: '6px 0', display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ color: '#60a5fa' }}>✓</span> {item}</li>
              ))}
            </ul>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ width: '100%', padding: '14px', fontSize: '1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start 7-Day Free Trial</button>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── FINAL CTA ─── */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div className="landing-orbs"><div className="landing-orb landing-orb--2" style={{ bottom: '20%', left: '10%' }} /><div className="landing-orb landing-orb--3" style={{ top: '10%', right: '10%' }} /></div>
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, marginBottom: 24 }}>Ready to <span className="text-gradient-crm">Close More Deals</span>?</h2>
          <p style={{ fontSize: '1.2rem', color: '#94a3b8', marginBottom: 40 }}>Join sales teams using AI to increase win rates by 78% — for just $49/month.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ padding: '16px 40px', fontSize: '1.1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start Free Trial →</button>
            <button onClick={() => navigate('/login')} style={{ padding: '16px 40px', fontSize: '1.1rem', fontWeight: 500, color: '#94a3b8', background: 'transparent', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 12, cursor: 'pointer' }}>Sign In</button>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer style={{ borderTop: '1px solid rgba(59,130,246,0.15)', padding: '2rem 1.5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: 'linear-gradient(135deg, #3b82f6, #60a5fa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem' }}>🎯</div>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Executive Force AI</span>
          </div>
          <p style={{ fontSize: '0.75rem', color: '#475569', margin: 0 }}>© {new Date().getFullYear()} Executive Force AI Inc. All rights reserved.</p>
          <div style={{ display: 'flex', gap: 24 }}>
            <button onClick={() => navigate('/login')} style={{ fontSize: '0.75rem', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>Sign In</button>
            <a href="https://executiveforceai.com/privacy-policy" style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>Privacy</a>
            <a href="https://executiveforceai.com/terms-conditions" style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
